import { apiClient } from "@/lib/apiClient";

const BASE = "/api/admin/reports";

/** Ortak wrapper: TransformResponseInterceptor { data: <payload> } döner. */
function unwrap<T>(body: { data?: T } | T): T {
  if (body && typeof body === "object" && "data" in (body as object)) {
    const d = (body as { data?: T }).data;
    if (d !== undefined) return d;
  }
  return body as T;
}

export interface CertificatePipelineItem {
  enrollmentID: string;
  certificateID: string | null;
  userID: string;
  userFullName: string;
  department: string | null;
  educationID: string;
  educationTitle: string;
  completedAt: string | null;
  issuedAt: string | null;
  ageDays: number | null;
  status: "ISSUED" | "PENDING";
  isBacklogged: boolean;
}

export interface CertificatePipelineSummary {
  total: number;
  issued: number;
  pending: number;
  backlogOver30Days: number;
  avgIssueDays: number;
  issueRate: number;
}

export interface CertificatePipelineBucket {
  month: string;
  issued: number;
  pending: number;
}

export interface CertificatePipelineResponse {
  summary: CertificatePipelineSummary;
  buckets: CertificatePipelineBucket[];
  items: CertificatePipelineItem[];
  count: number;
}

export interface CertificatePipelineParams {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  status?: "ALL" | "ISSUED" | "PENDING";
  search?: string;
  departmentID?: string;
}

// Intern Placement
export interface InternPlacementItem {
  id: string;
  fullName: string;
  school: string;
  schoolDepartment: string;
  grade: string;
  department: string | null;
  status: number;
  internshipType: number;
  documentStatus: number;
  startDate: string;
  endDate: string;
  email: string | null;
  phone: string | null;
}

export interface InternPlacementBreakdown {
  label: string;
  count: number;
}

export interface InternPlacementResponse {
  summary: {
    total: number;
    active: number;
    completed: number;
    pendingDocs: number;
    completionRate: number;
  };
  byStatus: InternPlacementBreakdown[];
  bySchool: InternPlacementBreakdown[];
  byDepartment: InternPlacementBreakdown[];
  byType: InternPlacementBreakdown[];
  /** Yeni spec: Zorunlu Yaz / İş Yeri / Beceri dağılımı */
  bySubCategory?: InternPlacementBreakdown[];
  /** Yeni spec: Merkez / Taşra dağılımı */
  byInternshipLocation?: InternPlacementBreakdown[];
  /** Yeni spec: Kariyer Kapısı / Şahsen dağılımı */
  byApplicationSource?: InternPlacementBreakdown[];
  items: InternPlacementItem[];
  count: number;
}

export interface InternPlacementParams {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  departmentID?: string;
  status?: number;
  internshipType?: number;
  /** Yeni spec filtreleri */
  subCategory?: number;
  internshipLocation?: number;
  applicationSource?: number;
}

// Budget Variance
export interface BudgetVarianceItem {
  educationID: string;
  title: string;
  plannedBudget: number;
  actualCost: number;
  enrollmentCount: number;
  completedCount: number;
  costPerParticipant: number;
  variance: number;
  variancePercent: number;
  isBudgetApproved: boolean;
}

export interface BudgetVarianceResponse {
  summary: {
    totalPlanned: number;
    totalActual: number;
    overallVariancePercent: number;
    educationCount: number;
  };
  items: BudgetVarianceItem[];
  count: number;
}

export interface BudgetVarianceParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  approvedOnly?: boolean;
}

// Department Investment
export interface DepartmentInvestmentItem {
  departmentID: string;
  name: string;
  userCount: number;
  enrollmentCount: number;
  completedCount: number;
  totalEducationHours: number;
  totalCost: number;
  costPerUser: number;
  completionRate: number;
}

export interface DepartmentInvestmentResponse {
  summary: {
    totalCost: number;
    totalHours: number;
    avgCostPerUser: number;
    avgCompletionRate: number;
  };
  items: DepartmentInvestmentItem[];
  count: number;
}

export interface DepartmentInvestmentParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  departmentID?: string;
}

// Survey Satisfaction
export interface SurveySatisfactionItem {
  educationID: string;
  educationTitle: string;
  surveyID: string | null;
  surveyName: string | null;
  responseCount: number;
  uniqueRespondents: number;
  avgRating: number;
  csat: number;
  npsScore: number;
  distribution: Record<string, number>;
}

export interface SurveySatisfactionResponse {
  summary: {
    totalResponses: number;
    avgRating: number;
    avgNps: number;
    respondedEducations: number;
  };
  items: SurveySatisfactionItem[];
  count: number;
}

export interface SurveySatisfactionParams {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  educationID?: string;
  search?: string;
}

// Virtual Classroom
export interface VirtualClassroomItem {
  classroomID: string;
  name: string | null;
  educationID: string;
  educationTitle: string;
  startDate: string | null;
  endDate: string | null;
  expectedParticipants: number;
  sessionCount: number;
  totalDurationMinutes: number;
  avgSessionDurationMinutes: number;
  actualParticipants: number;
  attendanceRate: number;
}

export interface VirtualClassroomResponse {
  summary: {
    totalClassrooms: number;
    totalSessions: number;
    totalDurationHours: number;
    avgAttendanceRate: number;
  };
  items: VirtualClassroomItem[];
  count: number;
}

export interface VirtualClassroomParams {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  educationID?: string;
  search?: string;
}

// Instructor Performance
export interface InstructorPerformanceItem {
  instructorID: string;
  fullName: string;
  email: string | null;
  educationCount: number;
  totalParticipants: number;
  completedCount: number;
  avgExamScore: number;
  passRate: number;
  avgSurveyRating: number;
  npsScore: number;
  avgCompletionDays: number;
}

export interface InstructorPerformanceResponse {
  summary: {
    totalInstructors: number;
    totalParticipants: number;
    avgExamScore: number;
    avgSurveyRating: number;
  };
  items: InstructorPerformanceItem[];
  count: number;
}

export interface InstructorPerformanceParams {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  instructorID?: string;
}

// Mandatory Compliance
export interface MandatoryComplianceItem {
  userID: string;
  fullName: string;
  email: string | null;
  department: string | null;
  departmentID: string | null;
  requiredEducations: number;
  completedEducations: number;
  missingEducations: { educationID: string; title: string }[];
  complianceRate: number;
  isCompliant: boolean;
}

export interface DepartmentCompliance {
  departmentID: string | null;
  name: string;
  userCount: number;
  compliantCount: number;
  complianceRate: number;
}

export interface MandatoryComplianceResponse {
  summary: {
    totalUsers: number;
    compliantUsers: number;
    nonCompliantUsers: number;
    overallComplianceRate: number;
    mandatoryEducationCount: number;
  };
  byDepartment: DepartmentCompliance[];
  items: MandatoryComplianceItem[];
  count: number;
}

export interface MandatoryComplianceParams {
  page?: number;
  limit?: number;
  search?: string;
  departmentID?: string;
  nonCompliantOnly?: boolean;
}

// Exam Item Analysis
export interface ExamItemAnalysisItem {
  questionID: string;
  questionText: string;
  attempts: number;
  correctCount: number;
  correctRate: number;
  difficultyIndex: number;
  discriminationIndex: number;
  classification: "easy" | "moderate" | "hard" | "very-hard";
}

export interface ExamItemAnalysisResponse {
  summary: {
    examID: string;
    examTitle: string;
    totalAttempts: number;
    totalQuestions: number;
    avgDifficulty: number;
    problematicCount: number;
  };
  items: ExamItemAnalysisItem[];
  count: number;
}

export interface ExamItemAnalysisParams {
  page?: number;
  limit?: number;
  examID?: string;
  search?: string;
}

export interface ExamListItem {
  id: string;
  title: string;
  questionCount: number;
}

// Learning Paths
export interface PathStep {
  educationID: string;
  title: string;
  order: number;
  enrolledCount: number;
  completedCount: number;
  completionRate: number;
  dropoffFromPrevious: number;
}

export interface LearningPathItem {
  pathID: string;
  startEducationID: string;
  startEducationTitle: string;
  stepCount: number;
  totalEnrollments: number;
  overallCompletionRate: number;
  avgDurationDays: number;
  steps: PathStep[];
}

export interface LearningPathsResponse {
  summary: {
    totalPaths: number;
    avgStepCount: number;
    overallCompletionRate: number;
  };
  items: LearningPathItem[];
  count: number;
}

export interface LearningPathsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export const reportsExtendedApi = {
  getCertificatePipeline: async (
    params: CertificatePipelineParams,
    signal?: AbortSignal,
  ): Promise<CertificatePipelineResponse> => {
    const { data } = await apiClient.get(`${BASE}/certificate-pipeline`, {
      params,
      signal,
    });
    return unwrap<CertificatePipelineResponse>(data);
  },

  getInternPlacement: async (
    params: InternPlacementParams,
    signal?: AbortSignal,
  ): Promise<InternPlacementResponse> => {
    const { data } = await apiClient.get(`${BASE}/intern-placement`, {
      params,
      signal,
    });
    return unwrap<InternPlacementResponse>(data);
  },

  getBudgetVariance: async (
    params: BudgetVarianceParams,
    signal?: AbortSignal,
  ): Promise<BudgetVarianceResponse> => {
    const { data } = await apiClient.get(`${BASE}/budget-variance`, {
      params,
      signal,
    });
    return unwrap<BudgetVarianceResponse>(data);
  },

  getDepartmentInvestment: async (
    params: DepartmentInvestmentParams,
    signal?: AbortSignal,
  ): Promise<DepartmentInvestmentResponse> => {
    const { data } = await apiClient.get(`${BASE}/department-investment`, {
      params,
      signal,
    });
    return unwrap<DepartmentInvestmentResponse>(data);
  },

  getSurveySatisfaction: async (
    params: SurveySatisfactionParams,
    signal?: AbortSignal,
  ): Promise<SurveySatisfactionResponse> => {
    const { data } = await apiClient.get(`${BASE}/survey-satisfaction`, {
      params,
      signal,
    });
    return unwrap<SurveySatisfactionResponse>(data);
  },

  getVirtualClassroom: async (
    params: VirtualClassroomParams,
    signal?: AbortSignal,
  ): Promise<VirtualClassroomResponse> => {
    const { data } = await apiClient.get(`${BASE}/virtual-classroom`, {
      params,
      signal,
    });
    return unwrap<VirtualClassroomResponse>(data);
  },

  getInstructorPerformance: async (
    params: InstructorPerformanceParams,
    signal?: AbortSignal,
  ): Promise<InstructorPerformanceResponse> => {
    const { data } = await apiClient.get(`${BASE}/instructor-performance`, {
      params,
      signal,
    });
    return unwrap<InstructorPerformanceResponse>(data);
  },

  getMandatoryCompliance: async (
    params: MandatoryComplianceParams,
    signal?: AbortSignal,
  ): Promise<MandatoryComplianceResponse> => {
    const { data } = await apiClient.get(`${BASE}/mandatory-compliance`, {
      params,
      signal,
    });
    return unwrap<MandatoryComplianceResponse>(data);
  },

  getExamItemAnalysis: async (
    params: ExamItemAnalysisParams,
    signal?: AbortSignal,
  ): Promise<ExamItemAnalysisResponse> => {
    const { data } = await apiClient.get(`${BASE}/exam-item-analysis`, {
      params,
      signal,
    });
    return unwrap<ExamItemAnalysisResponse>(data);
  },

  listExamsForReports: async (
    search?: string,
    signal?: AbortSignal,
  ): Promise<ExamListItem[]> => {
    const { data } = await apiClient.get(`${BASE}/exam-list`, {
      params: search ? { search } : undefined,
      signal,
    });
    return unwrap<ExamListItem[]>(data);
  },

  getLearningPaths: async (
    params: LearningPathsParams,
    signal?: AbortSignal,
  ): Promise<LearningPathsResponse> => {
    const { data } = await apiClient.get(`${BASE}/learning-paths`, {
      params,
      signal,
    });
    return unwrap<LearningPathsResponse>(data);
  },

  exportReport: async (payload: {
    reportType: string;
    format: "xlsx" | "pdf";
    filters?: Record<string, unknown>;
  }): Promise<Blob> => {
    const res = await apiClient.post<Blob>(
      `${BASE}/export`,
      {},
      {
        params: {
          reportType: payload.reportType,
          format: payload.format,
          ...(payload.filters ?? {}),
        },
        responseType: "blob",
      },
    );
    return res.data as Blob;
  },
};
